// frontend/src/pages/wallet/WalletPage.js

import React, { useState, useEffect, useRef } from 'react';
import { 
  Wallet, 
  Plus, 
  History, 
  TrendingUp, 
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  Download,
  ChevronRight,
  ArrowUpRight,
  CreditCard,
  BarChart3,
  Copy,
  ExternalLink,
} from 'lucide-react';
import FundWalletModal from './components/FundWalletModal';
import TransactionDetailsModal from './components/TransactionDetailsModal';
import VirtualAccountModal from '../../components/wallet/VirtualAccountModal';
import useWalletStore from '../../store/walletStore';
import useAuthStore from '../../store/authStore';
import { toast } from 'react-hot-toast';

const WalletPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showFundModal, setShowFundModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [showVirtualAccountModal, setShowVirtualAccountModal] = useState(false);

  const {
    wallet,
    transactions,
    transactionSummary,
    virtualAccount,
    virtualAccountLoading,
    getWallet,
    getTransactions,
    getTransactionSummary,
    getVirtualAccountDetails,
  } = useWalletStore();
  const { user } = useAuthStore();

  const hasLoadedData = useRef(false);

  useEffect(() => {
    if (!hasLoadedData.current) {
      hasLoadedData.current = true;
      loadData();
    }
  }, []);

  const loadData = async () => {
    try {
      await Promise.all([
        getWallet(),
        getTransactions(),
        getTransactionSummary(),
        getVirtualAccountDetails(),
      ]);
    } catch (error) {
      console.error('Failed to load wallet data:', error);
    }
  };

  const handleFundSuccess = () => {
    loadData();
    setShowFundModal(false);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const transactionDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const diffTime = today - transactionDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'text-green-600 bg-green-50';
      case 'PENDING':
        return 'text-yellow-600 bg-yellow-50';
      case 'FAILED':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="h-3.5 w-3.5" />;
      case 'PENDING':
        return <Clock className="h-3.5 w-3.5" />;
      case 'FAILED':
        return <XCircle className="h-3.5 w-3.5" />;
      default:
        return null;
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    if (transactionFilter === 'all') return true;
    if (transactionFilter === 'deposits') return transaction.type === 'CREDIT';
    if (transactionFilter === 'withdrawals') return transaction.type === 'DEBIT';
    return transaction.status === transactionFilter;
  });

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'transactions', name: 'Transactions', icon: History },
  ];

  const filterOptions = [
    { id: 'all', label: 'All Transactions', color: 'primary' },
    { id: 'deposits', label: 'Deposits Only', color: 'green' },
    { id: 'withdrawals', label: 'Withdrawals Only', color: 'orange' },
    { id: 'PENDING', label: 'Pending', color: 'yellow' },
    { id: 'SUCCESS', label: 'Successful', color: 'green' }
  ];

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  return (
    <div className="min-h-screen bg-[#E8ECF5] p-3 sm:p-4 md:p-5">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph mb-3 border border-primary-100/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-1 h-4 sm:h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full" />
              <h1 className="text-base sm:text-lg font-bold text-gray-800">Wallet</h1>
            </div>
            <button
              onClick={() => setShowFundModal(true)}
              className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-xs sm:text-sm font-medium flex items-center"
            >
              <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
              Fund Wallet
            </button>
          </div>
        </div>

        {/* Main Card - Balance + Virtual Account */}
        <div className="bg-white rounded-xl shadow-neomorph border border-primary-100/30 mb-3 sm:mb-4 overflow-hidden">
          {/* Balance Section */}
          <div className="p-4 sm:p-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg shadow-md">
                  <Wallet className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Available Balance</p>
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
                    {formatAmount(wallet?.balance)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400">Fee: 2% (max ₦50)</p>
                <p className="text-[10px] text-gray-400">
                  Updated: {wallet ? formatDate(wallet.updatedAt) : '...'}
                </p>
              </div>
            </div>
          </div>

          {/* Virtual Account Section - With Full Labels */}
          <div className="p-4 sm:p-5">
            {virtualAccountLoading && !virtualAccount ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                <span className="ml-2 text-sm text-gray-500">Loading virtual account...</span>
              </div>
            ) : virtualAccount && virtualAccount.accountNumber ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <CreditCard className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-semibold text-gray-800">Virtual Account</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Active
                    </span>
                  </div>
                </div>
                
                <div className="bg-[#E8ECF5] rounded-xl p-4 shadow-neomorph-inset space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Account Number</p>
                    <div className="flex items-center justify-between mt-0.5">
                      <p className="text-base sm:text-lg font-bold text-gray-800 font-mono">
                        {virtualAccount.accountNumber}
                      </p>
                      <button
                        onClick={() => copyToClipboard(virtualAccount.accountNumber, 'Account number')}
                        className="bg-white rounded-lg p-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all flex-shrink-0 ml-2"
                      >
                        <Copy className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Bank</p>
                    <p className="text-sm sm:text-base font-medium text-gray-800 mt-0.5">
                      {virtualAccount.bankName}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">Account Name</p>
                    <p className="text-sm sm:text-base font-medium text-gray-800 mt-0.5 truncate">
                      {virtualAccount.accountName}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center mt-3 text-sm text-blue-600">
                  <ExternalLink className="h-4 w-4 mr-1.5" />
                  <span>Transfer to this account and funds will be credited automatically</span>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center mb-3">
                  <CreditCard className="h-5 w-5 text-primary-600 mr-2" />
                  <span className="text-sm font-semibold text-gray-800">Virtual Account</span>
                </div>
                
                <div className="bg-blue-50 rounded-xl p-4 shadow-neomorph-inset border border-blue-200/50">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-blue-800 mb-1">Get a dedicated bank account</h4>
                      <p className="text-xs text-blue-700">
                        Create a virtual account to receive bank transfers directly to your Fastel wallet.
                        Funds are credited automatically.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowVirtualAccountModal(true)}
                      className="flex-shrink-0 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-5 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium"
                    >
                      <CreditCard className="h-4 w-4 inline mr-2" />
                      Get Virtual Account
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg p-1 shadow-neomorph mb-3 sm:mb-4 overflow-x-auto">
          <div className="flex min-w-max sm:min-w-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium flex items-center transition-all whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30'
                    : 'text-gray-600 hover:text-gray-800'
                  }
                `}
              >
                <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && (
            <div className="space-y-3 sm:space-y-4">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] sm:text-xs text-gray-600 mb-1">Total Deposits (30d)</p>
                      <p className="text-sm sm:text-lg font-semibold text-gray-800 truncate">
                        {formatAmount(transactionSummary?.summary?.totalDeposits || 0)}
                      </p>
                      <p className="text-[10px] sm:text-xs text-gray-500 mt-1 truncate">
                        {transactionSummary?.summary?.depositCount || 0} transactions
                      </p>
                    </div>
                    <div className="p-1.5 sm:p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-md shadow-md flex-shrink-0 ml-2">
                      <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] sm:text-xs text-gray-600 mb-1">Net Balance (30d)</p>
                      <p className="text-sm sm:text-lg font-semibold text-gray-800 truncate">
                        {formatAmount(transactionSummary?.summary?.netFlow || 0)}
                      </p>
                      <p className={`text-[10px] sm:text-xs mt-1 truncate ${(transactionSummary?.summary?.netFlow || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {(transactionSummary?.summary?.netFlow || 0) >= 0 ? 'Positive' : 'Negative'} flow
                      </p>
                    </div>
                    <div className="p-1.5 sm:p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md shadow-md flex-shrink-0 ml-2">
                      <History className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Transactions */}
              <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div className="flex items-center min-w-0 flex-1">
                    <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2 flex-shrink-0" />
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-800 truncate">Recent Transactions</h3>
                  </div>
                  <button
                    onClick={() => setActiveTab('transactions')}
                    className="text-[10px] sm:text-xs font-medium text-primary-600 hover:text-primary-700 flex items-center flex-shrink-0 ml-2"
                  >
                    View all
                    <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 ml-0.5 sm:ml-1" />
                  </button>
                </div>

                <div className="space-y-2">
                  {transactionSummary?.recentTransactions?.length > 0 ? (
                    transactionSummary.recentTransactions.slice(0, 5).map((transaction) => (
                      <div
                        key={transaction.id}
                        onClick={() => setSelectedTransaction(transaction)}
                        className="flex items-center justify-between p-2 sm:p-3 rounded-lg bg-[#E8ECF5] shadow-neomorph-inset cursor-pointer hover:shadow-neomorph transition-all"
                      >
                        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                          <div className={`p-1.5 sm:p-2 rounded-md flex-shrink-0 ${(
                            transaction.type === 'CREDIT' ? 'bg-green-50' : 'bg-gray-50'
                          )}`}>
                            <ArrowUpRight className={`h-3 w-3 sm:h-4 sm:w-4 ${
                              transaction.type === 'CREDIT' ? 'text-green-600' : 'text-gray-600'
                            }`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="text-xs sm:text-sm font-medium text-gray-800 truncate">
                              {transaction.description || `${transaction.type} Transaction`}
                            </h4>
                            <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                              {formatDate(transaction.createdAt)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <p className={`text-xs sm:text-sm font-medium ${
                            transaction.type === 'CREDIT' ? 'text-green-600' : 'text-gray-600'
                          }`}>
                            {transaction.type === 'CREDIT' ? '+' : '-'}{formatAmount(transaction.amount)}
                          </p>
                          <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[9px] sm:text-xs font-medium ${getStatusColor(transaction.status)}`}>
                            {getStatusIcon(transaction.status)}
                            <span className="ml-0.5 sm:ml-1 hidden sm:inline">{transaction.status}</span>
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 sm:py-8">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3">
                        <History className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500 mb-2">No recent transactions</p>
                      <button
                        onClick={() => setShowFundModal(true)}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg text-[10px] sm:text-xs shadow-md shadow-primary-500/30"
                      >
                        Fund Wallet
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="space-y-3 sm:space-y-4">
              {/* Filters */}
              <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between sm:justify-start sm:space-x-4">
                    <div className="flex items-center space-x-2">
                      <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                      <span className="text-[10px] sm:text-xs font-medium text-gray-700">Filter by:</span>
                    </div>
                    
                    <button
                      onClick={() => setShowFilterOptions(!showFilterOptions)}
                      className="sm:hidden flex items-center space-x-2 px-3 py-1.5 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset"
                    >
                      <span className="text-xs font-medium text-gray-700">
                        {filterOptions.find(f => f.id === transactionFilter)?.label || 'All Transactions'}
                      </span>
                      <ChevronRight className={`h-3.5 w-3.5 text-gray-500 transition-transform ${showFilterOptions ? 'rotate-90' : ''}`} />
                    </button>
                    
                    <div className="hidden sm:flex flex-wrap gap-2">
                      {filterOptions.map((filter) => (
                        <button
                          key={filter.id}
                          onClick={() => setTransactionFilter(filter.id)}
                          className={`
                            px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                            ${transactionFilter === filter.id
                              ? filter.color === 'primary'
                                ? 'bg-primary-50 text-primary-700 shadow-neomorph-inset border border-primary-200'
                                : filter.color === 'green'
                                ? 'bg-green-50 text-green-700 shadow-neomorph-inset border border-green-200'
                                : filter.color === 'orange'
                                ? 'bg-orange-50 text-orange-700 shadow-neomorph-inset border border-orange-200'
                                : 'bg-yellow-50 text-yellow-700 shadow-neomorph-inset border border-yellow-200'
                              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                            }
                          `}
                        >
                          {filter.label}
                        </button>
                      ))}
                    </div>

                    <button className="hidden sm:flex bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 items-center border border-gray-100">
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      Export
                    </button>
                  </div>

                  {showFilterOptions && (
                    <div className="sm:hidden grid grid-cols-2 gap-2 pt-2">
                      {filterOptions.map((filter) => (
                        <button
                          key={filter.id}
                          onClick={() => {
                            setTransactionFilter(filter.id);
                            setShowFilterOptions(false);
                          }}
                          className={`
                            px-3 py-2 rounded-lg text-xs font-medium transition-all text-left
                            ${transactionFilter === filter.id
                              ? filter.color === 'primary'
                                ? 'bg-primary-50 text-primary-700 shadow-neomorph-inset border border-primary-200'
                                : filter.color === 'green'
                                ? 'bg-green-50 text-green-700 shadow-neomorph-inset border border-green-200'
                                : filter.color === 'orange'
                                ? 'bg-orange-50 text-orange-700 shadow-neomorph-inset border border-orange-200'
                                : 'bg-yellow-50 text-yellow-700 shadow-neomorph-inset border border-yellow-200'
                              : 'bg-white text-gray-600 border border-gray-200'
                            }
                          `}
                        >
                          {filter.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Transactions List */}
              <div className="bg-white rounded-lg p-3 sm:p-4 shadow-neomorph border border-gray-100">
                {/* Mobile: Card view */}
                <div className="sm:hidden space-y-3">
                  {filteredTransactions.length > 0 ? (
                    filteredTransactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        onClick={() => setSelectedTransaction(transaction)}
                        className="p-3 rounded-lg bg-[#E8ECF5] shadow-neomorph-inset cursor-pointer hover:shadow-neomorph transition-all"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <div className={`p-1.5 rounded-md flex-shrink-0 ${
                              transaction.type === 'CREDIT' ? 'bg-green-50' : 'bg-gray-50'
                            }`}>
                              <ArrowUpRight className={`h-3 w-3 ${
                                transaction.type === 'CREDIT' ? 'text-green-600' : 'text-gray-600'
                              }`} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-gray-800 truncate">
                                {transaction.description || 'Wallet Transaction'}
                              </p>
                              <p className="text-[10px] text-gray-500">
                                {formatDate(transaction.createdAt)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 ml-2">
                            <p className={`text-sm font-semibold ${
                              transaction.type === 'CREDIT' ? 'text-green-600' : 'text-gray-600'
                            }`}>
                              {transaction.type === 'CREDIT' ? '+' : '-'}{formatAmount(transaction.amount)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-medium ${getStatusColor(transaction.status)}`}>
                            {getStatusIcon(transaction.status)}
                            <span className="ml-1">{transaction.status}</span>
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[9px] font-medium ${
                            transaction.type === 'CREDIT'
                              ? 'bg-green-50 text-green-700'
                              : 'bg-gray-50 text-gray-700'
                          }`}>
                            {transaction.type}
                          </span>
                          <span className="text-[9px] text-gray-400 font-mono">
                            {transaction.reference?.slice(0, 8)}...
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center mx-auto mb-2">
                        <History className="h-5 w-5 text-primary-600" />
                      </div>
                      <p className="text-xs text-gray-500">No transactions found</p>
                      <p className="text-[10px] text-gray-400 mt-1">Try changing your filters</p>
                    </div>
                  )}
                </div>

                {/* Desktop: Table view */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reference
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredTransactions.length > 0 ? (
                        filteredTransactions.map((transaction) => (
                          <tr
                            key={transaction.id}
                            onClick={() => setSelectedTransaction(transaction)}
                            className="hover:bg-[#E8ECF5] cursor-pointer transition-colors"
                          >
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-800">
                                {transaction.description || 'Wallet Transaction'}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-xs text-gray-500">
                                {formatDate(transaction.createdAt)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className={`text-sm font-medium ${
                                transaction.type === 'CREDIT' ? 'text-green-600' : 'text-gray-600'
                              }`}>
                                {transaction.type === 'CREDIT' ? '+' : '-'}{formatAmount(transaction.amount)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                transaction.type === 'CREDIT'
                                  ? 'bg-green-50 text-green-700'
                                  : 'bg-gray-50 text-gray-700'
                              }`}>
                                {transaction.type}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(transaction.status)}`}>
                                {getStatusIcon(transaction.status)}
                                <span className="ml-1">{transaction.status}</span>
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-xs text-gray-500 font-mono">
                                {transaction.reference?.slice(0, 8)}...
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="px-4 py-12 text-center">
                            <div className="text-gray-500">
                              <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center mx-auto mb-3">
                                <History className="h-6 w-6 text-primary-600" />
                              </div>
                              <p className="text-sm text-gray-500">No transactions found</p>
                              <p className="text-xs text-gray-400 mt-1">Try changing your filters</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modals */}
        {showFundModal && (
          <FundWalletModal
            onClose={() => setShowFundModal(false)}
            onSuccess={handleFundSuccess}
          />
        )}

        {selectedTransaction && (
          <TransactionDetailsModal
            transaction={selectedTransaction}
            onClose={() => setSelectedTransaction(null)}
          />
        )}

        {showVirtualAccountModal && (
          <VirtualAccountModal
            onClose={() => setShowVirtualAccountModal(false)}
          />
        )}
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default WalletPage;